import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { api, type CarouselItem, type CarouselSlide } from "@/lib/api";
import { OP_SURFACE } from "@/lib/opSurfaces";
import { cn } from "@/lib/utils";

function slideByIndex(slides: CarouselSlide[] | undefined, index: number): CarouselSlide | undefined {
  return (slides || []).find((s) => Number(s.index_n) === index);
}

export default function LiveSlideCarousel({
  item,
  total,
  done,
  running,
  onSelect,
  className,
}: {
  item?: CarouselItem | null;
  total: number;
  done?: number;
  running?: boolean;
  onSelect?: (index: number) => void;
  className?: string;
}) {
  const slotRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const slideCount = Math.max(total || 0, (item?.slides || []).length, 0);
  const slots = slideCount > 0 ? Array.from({ length: slideCount }, (_, i) => i + 1) : [];
  const bustBase = item?.render_token || item?.updated_at || item?.render_done || "";
  const latestDone = Math.max(0, Number(done ?? item?.render_done ?? 0));

  useEffect(() => {
    if (!latestDone) return;
    const node = slotRefs.current[latestDone];
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
  }, [latestDone]);

  if (!slots.length) {
    return (
      <div className={cn("rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 p-6 text-sm text-slate-600", className)}>
        {running ? "Czekam na pierwszy slajd…" : "Tu pojawi się karuzela po złożeniu obrazków."}
      </div>
    );
  }

  return (
    <div className={cn(running ? OP_SURFACE.process.box : OP_SURFACE.result.box, "p-3", className)}>
      <div className="mb-2 flex items-center justify-between gap-2 px-1">
        <p className={cn("text-xs font-medium", running ? OP_SURFACE.process.ink : OP_SURFACE.result.ink)}>
          {running ? `Składam karuzelę · ${latestDone}/${slideCount}` : `Karuzela · ${slideCount} PNG`}
        </p>
        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-700" /> : null}
      </div>
      <div
        className="flex gap-3 overflow-x-auto pb-1 scroll-smooth"
        style={{ scrollbarGutter: "stable" }}
      >
        {slots.map((index) => {
          const slide = slideByIndex(item?.slides, index);
          const ready = Boolean(slide?.png_relpath || slide?.id);
          const isCurrent = running && index === latestDone + 1 && !ready;
          const isNewest = ready && index === latestDone;
          const qaFail = slide?.qa_ok === false;
          const canOpen = ready && Boolean(onSelect) && !running;

          return (
            <button
              key={index}
              type="button"
              ref={(el) => {
                slotRefs.current[index] = el;
              }}
              disabled={!canOpen}
              onClick={() => {
                if (canOpen) onSelect?.(index);
              }}
              className={cn(
                "group relative w-[15rem] shrink-0 overflow-hidden rounded-lg border bg-white text-left transition sm:w-72",
                qaFail
                  ? "border-2 border-red-600"
                  : isNewest
                    ? "border-2 border-brand ring-2 ring-brand/25"
                    : ready
                      ? "border-border hover:border-brand/50"
                      : "border-dashed border-slate-300",
                canOpen ? "cursor-pointer" : "cursor-default",
              )}
            >
              {ready && item?.id ? (
                <img
                  src={`${api.carouselSlidePng(item.id, index)}?v=${bustBase}-${index}`}
                  alt={`slajd ${index}`}
                  className="aspect-[4/5] w-full bg-muted object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.opacity = "0.25";
                  }}
                />
              ) : (
                <div
                  className={cn(
                    "flex aspect-[4/5] w-full flex-col items-center justify-center gap-1 bg-slate-100 text-slate-500",
                    isCurrent && "animate-pulse bg-cyan-50 text-cyan-800",
                  )}
                >
                  {isCurrent ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span className="text-lg font-semibold tabular-nums">{index}</span>
                  <span className="px-2 text-center text-[10px] leading-tight">
                    {isCurrent ? "składam…" : "czekam"}
                  </span>
                </div>
              )}
              <div className="border-t border-border/60 px-2 py-1.5">
                <p className="truncate text-xs text-muted-foreground">
                  {index}. {slide?.slide_type || "slajd"}
                </p>
                {qaFail && slide?.qa_note ? (
                  <p className="mt-0.5 line-clamp-2 text-[11px] text-red-700">{slide.qa_note}</p>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
